/** One apartment level, drawn the way A-37 (bloc C5) draws one.
 *
 * The floors above ground are all identical — the ribbon, the flats and the
 * cores repeat unchanged from 0 to the top — because that is what makes the
 * structure stack: a column runs from the raft to the roof and may only stand
 * inside a wall. So this renders one level's geometry and the level number is
 * only a label.
 *
 * Rooms are labelled name over area, furnished, and the corridor is the salmon
 * the reference plan uses. The furniture is not decoration: an 18 m² living room
 * that cannot hold a sofa and a table, or a 12 m² bedroom that cannot hold a bed
 * with a way round it, is a room that passed on area and fails in use — drawing
 * the furniture is what makes that visible on the plan.
 */
import type { Pt } from "../types";
import type { SiteEntrance } from "../client";
import { pickAttrs, type PickAttrs, type Selection } from "../inspect";
import { DECOR, SEL_STROKE } from "./glyphs";
import { numeleFelului } from "../vocabular";

export type ApartmentRoom = {
  /** The canonical document's own `id` for this room ("rm_d1f6874c333c5806").
   *
   *  Carried through from the document because it is the STABLE identity, and
   *  the array position is not: the validator's report refers to rooms by id, and
   *  resolving that against a position would mis-highlight the moment a re-solve
   *  reorders anything. Optional because a legacy saved project has no ids.
   *
   *  Named `eid` and not `id` throughout the view models, because
   *  `ApartmentBuilding.id` already means the block's CODE ("C1") and one of the
   *  two would have had to change meaning. */
  eid?: string;
  poly: Pt[] | number[][];
  kind: string;
  label: string;
  area?: number;
  /** Legea 114/1996 anexa 1's floor for this room. Optional because a project
   *  saved before the inspector existed has no such field. */
  min_area?: number;
};

/** One door leaf: the two jambs of the opening, the hinge, and the open tip.
 *  Everything the swing arc needs, so the renderer never re-derives which side
 *  of the wall the room is on. */
export type ApartmentDoor = {
  /** The document's `id` for this opening. See `ApartmentRoom.eid`. */
  eid?: string;
  kind: string;
  label: string;
  w: number;
  hinge: number[];
  tip: number[];
  jambs: number[][];
};

export type ApartmentFlat = {
  /** The document's `id` for this dwelling. See `ApartmentRoom.eid`. */
  eid?: string;
  poly: number[][];
  rooms: number;
  /** The norm's occupancy for this dwelling type. `undefined` where no rule
   *  engine has run — the figure is `norm(mix entry)` and lives in
   *  `compliance.requirements`, not on the dwelling. */
  persons?: number;
  area: number;
  label: string;
  deg?: number;
  /** Balconul locuinței: poligonul celui mai mare, aria TUTUROR (anexa 1 le
   *  numără la un loc). */
  balcony?: { poly: number[][]; area: number; eid?: string } | null;
  /** …și fiecare în parte, de desenat: fâșia compoziției e scrisă pe
   *  locuințe, iar una de colț are câte o bucată pe fiecare față. */
  balconies?: { poly: number[][]; area: number; eid?: string }[];
  layout: ApartmentRoom[];
  doors?: ApartmentDoor[];
  /** Which block on the lot this dwelling belongs to ("C1"…). */
  building?: string | null;
  /** anexa 1 tab. B's useful-area minimum for this (camere, persoane). */
  useful_required?: number;
  /** Metres of facade the dwelling occupies — what the cutter is spending. */
  frontage?: number;
  /** True when this is the dwelling that took the band of depth beside a stair
   *  core, which is why it is an L and its neighbours are rectangles. */
  wraps_core?: boolean;
  /** Recompartimentarea (`props["architecture.recomp"]`): starea (`in_lucru` cât jobul
   *  încă rulează, `esuat`, sau gata — fără `stare`), câte variante păstrate și a câta
   *  e scrisă. Numai pe apartamentele atinse de holul de ieșire sau de retragere. */
  recomp?: { stare?: string; variante?: number; ales?: number; tip?: string } | null;
};

export type ApartmentCorePart = {
  /** The document's `id` for this core part — a supplier attachment lands on a
   *  lift shaft, not on "the core". See `ApartmentRoom.eid`. */
  eid?: string;
  poly: number[][];
  kind: string;
  label: string;
  area?: number;
  cx?: number;
  cy?: number;
  w?: number;
  h?: number;
  deg?: number;
};

export type ApartmentCore = {
  /** The document's `id` for the core itself — what a party wall toward the
   *  stair core names as the thing on its other side (`BAIE.R27`, `HOL.R18`).
   *  See `ApartmentRoom.eid`. */
  eid?: string;
  cx: number;
  cy: number;
  shared: boolean;
  deg?: number;
  building?: string | null;
  parts: ApartmentCorePart[];
};

/** The outline of one block on the lot, and what it holds. */
export type ApartmentBuilding = {
  /** The block CODE — "C1" — which is what the drawing calls it. Not the
   *  document's entity id; that is `eid`. */
  id: string;
  /** The document's `id` for this block. See `ApartmentRoom.eid`. */
  eid?: string;
  poly: number[][];
  flats: number;
  cores: number;
  /** Sumă dependentă de normă, citită din `compliance.reported` — un
   *  document care nu o raportează n-o are. Tipul spunea `number`, iar
   *  inspectorul o formata nepăzit: pe un document fără `reported`,
   *  `undefined.toFixed` lua tot ecranul. */
  useful_m2?: number;
  footprint_m2: number;
};

export type ApartmentLevel = {
  level: number;
  flats: ApartmentFlat[];
  /** `eid` ca la orice altă familie de aici — v. nota de la
   *  `ApartmentRoom.eid`. Bara era singura fără, iar fără ea inventarul
   *  documentului n-avea cum să scadă barele desenate din cele emise și
   *  le raporta drept nedesenate. */
  bars: { eid?: string; poly: number[][]; corridor: number[][] }[];
  cores: ApartmentCore[];
  /** One entry per BLOCK on the lot. The reference site carries four — C1..C4,
   *  a sheet pair each (A04-A09) — and the drawing names them, so a plan that
   *  renders one undifferentiated ribbon cannot be compared with it. */
  buildings?: ApartmentBuilding[];
  count: number;
  /** Sumă dependentă de normă, citită din `compliance.reported` — un
   *  document care nu o raportează n-o are. Tipul spunea `number`, iar
   *  inspectorul o formata nepăzit: pe un document fără `reported`,
   *  `undefined.toFixed` lua tot ecranul. */
  useful_m2?: number;
  shape?: string | null;
  separation?: number;
  columns_off_wall?: number;
};

/** Fill per dwelling type. Deliberately flat and low-saturation so the room
 *  divisions and the labels stay the thing you read first.
 *
 *  Stops at three camere, because the project builds nothing above three (see
 *  `catalog.MAX_ROOMS`). Keeping colours for 4 and 5 would have painted a
 *  leaked four-room dwelling as a legitimate part of the palette; it now falls
 *  to the grey below, where it is visible as the anomaly it would be. */
const FLAT_FILL: Record<number, string> = {
  1: "#cfe0ee",
  2: "#bcd3e6",
  3: "#e2d3b4",
};

/** Wet and service rooms read cooler than the habitable ones, as on A-37. */
const ROOM_FILL: Record<string, string> = {
  bath: "#dfe7ec",
  bath2: "#dfe7ec",
  wc: "#dfe7ec",
  shower: "#dfe7ec",
  hall: "#eceff1",
  store: "#e6e2da",
  kitchen: "#ededed",
};

const INK = "#33383d";
// The reference sheets (A-37; Bd. Timisoara A05) draw the plan three ways at
// once: walls as solid dark poche, room NAMES in brick red, and the area in
// black beneath the name. Matching that is most of what makes a drawing read as
// a drawing rather than as a diagram.
const WALL = "#4a4e52";        // poche
const ROOM_NAME = "#086a7a";   // the red the sheets label rooms in

/** Short names for the parts of a core, as the sheets letter them. Anything not
 *  here falls back to the part's own label.
 *
 *  Derived from the catalog rather than retyped: the sheets set them in caps,
 *  which is a drawing convention and the only thing this adds. A fourth
 *  hand-written copy of the vocabulary is what gap #5 exists to stop. */
/** NUMELE NUCLEULUI, ÎN LIMBA CELUI CARE SE UITĂ, nu în cea a catalogului.
 *
 *  Era un tabel făcut din `LABEL` la încărcarea modulului, deci: românesc
 *  întotdeauna («CASĂ DE SCARĂ», «ASCENSOR» în interfața englezească), și
 *  înghețat la prima limbă chiar dacă ar fi citit `t()`. Se compune acum la
 *  fiecare desen, din vocabularul care are amândouă limbile. Majusculele
 *  rămân: ele sunt convenția planșei, nu o traducere. */
const NUCLEU_IN_PLAN = ["scara", "hol", "lift", "ghena"];
const numeNucleu = (kind: string): string | null =>
  NUCLEU_IN_PLAN.includes(kind) ? numeleFelului(kind).toUpperCase() : null;
const AREA_INK = "#1a1d20";
const W_STRUCT = 0.40;         // party/facade wall, drawn at its real thickness
const W_PART = 0.12;           // interior partition
const FURN = "#7d848b";

/** Level of detail, in rendered pixels. Below each threshold the layer is not
 *  drawn at all, because a 4 px label or a 3 px sofa is noise rather than
 *  information — and forty flats of it is what reads as "crammed".
 *
 *  `furniture` is the room's SHORT side in px; `name` and `area` are the text's
 *  own height in px. */
const LOD = { furniture: 20, name: 6.5, area: 8.5, coreLabel: 7 };

function d(poly: number[][] | Pt[]): string {
  const pts = poly as number[][];
  if (!pts || pts.length < 3) return "";
  return pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ") + " Z";
}


/** A room's size in ITS OWN frame, not in world axes.
 *
 *  A ribbon turns corners, so half the flats on a level sit at 90° to the other
 *  half. Measuring a bounding box in world x/y would size a bed to the wrong
 *  edge on every one of them. */
function oriented(poly: number[][], deg: number) {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const [x, y] of poly) {
    const u = x * c + y * s, v = -x * s + y * c;
    if (u < u0) u0 = u; if (u > u1) u1 = u;
    if (v < v0) v0 = v; if (v > v1) v1 = v;
  }
  const cu = (u0 + u1) / 2, cv = (v0 + v1) / 2;
  return { cx: cu * c - cv * s, cy: cu * s + cv * c, w: u1 - u0, h: v1 - v0 };
}

const rect = (x: number, y: number, w: number, h: number, r = 0.06) => (
  <rect x={x} y={y} width={w} height={h} rx={r} fill="none"
        stroke={FURN} strokeWidth={0.06} />
);

/** Furniture, drawn in the room's own frame: origin at its centre, `w` along
 *  the facade and `h` from the corridor out to it. */
function Furniture({ kind, w, h, px }:
  { kind: string; w: number; h: number; px: number }) {
  if (w < 1.0 || h < 0.8) return null;
  if (Math.min(w, h) * px < LOD.furniture) return null;   // too small to read
  const hw = w / 2, hh = h / 2;
  switch (kind) {
    case "living": {
      const sw = Math.min(2.2, w * 0.62);
      return (
        <g>
          {/* sofa against the party wall, table in front of it */}
          {rect(-hw + 0.25, hh - 1.15, sw, 0.85, 0.12)}
          {rect(-hw + 0.25 + sw * 0.15, hh - 2.15, sw * 0.7, 0.6, 0.08)}
        </g>
      );
    }
    case "bedroom": {
      const bw = Math.min(1.6, w * 0.6), bh = Math.min(2.0, h * 0.55);
      return (
        <g>
          {/* bed head to the party wall, wardrobe on the opposite one */}
          {rect(-hw + 0.3, -hh + 0.3, bw, bh, 0.08)}
          <line x1={-hw + 0.3} y1={-hh + 0.75} x2={-hw + 0.3 + bw} y2={-hh + 0.75}
                stroke={FURN} strokeWidth={0.06} />
          {w > 2.6 && rect(hw - 0.85, -hh + 0.3, 0.6, Math.min(1.8, h * 0.5))}
        </g>
      );
    }
    case "kitchen": {
      const cw = Math.min(w - 0.4, 2.6);
      return (
        <g>
          {/* counter run along the corridor wall: sink, then two hob rings */}
          {rect(-cw / 2, hh - 0.65, cw, 0.6, 0.04)}
          {rect(-cw / 2 + 0.12, hh - 0.55, 0.42, 0.4, 0.06)}
          <circle cx={cw / 2 - 0.45} cy={hh - 0.35} r={0.14} fill="none"
                  stroke={FURN} strokeWidth={0.05} />
          <circle cx={cw / 2 - 0.15} cy={hh - 0.35} r={0.14} fill="none"
                  stroke={FURN} strokeWidth={0.05} />
        </g>
      );
    }
    case "bath":
    case "bath2":
      return (
        <g>
          {rect(-hw + 0.15, -hh + 0.15, Math.min(1.7, w - 0.3), Math.min(0.75, h - 0.3), 0.12)}
          <ellipse cx={hw - 0.35} cy={hh - 0.4} rx={0.22} ry={0.3} fill="none"
                   stroke={FURN} strokeWidth={0.05} />
        </g>
      );
    case "wc":
      return <ellipse cx={0} cy={0} rx={0.22} ry={0.3} fill="none"
                      stroke={FURN} strokeWidth={0.05} />;
    case "shower":
      return (
        <g>
          {rect(-hw + 0.15, -hh + 0.15, Math.min(0.9, w - 0.3), Math.min(0.9, h - 0.3))}
          <line x1={-hw + 0.15} y1={-hh + 0.15}
                x2={-hw + 0.15 + Math.min(0.9, w - 0.3)}
                y2={-hh + 0.15 + Math.min(0.9, h - 0.3)}
                stroke={FURN} strokeWidth={0.05} />
        </g>
      );
    case "store":
      return <line x1={-hw + 0.15} y1={0} x2={hw - 0.15} y2={0}
                   stroke={FURN} strokeWidth={0.06} strokeDasharray="0.3 0.2" />;
    default:
      return null;
  }
}

/** Name over area, as A-37 annotates every room. Shrinks to fit, and disappears
 *  rather than spilling out of a room too small to hold it — or being drawn at a
 *  zoom where it is 3 px tall and reads as dirt.
 *
 *  `px` is pixels per metre from the editor's own transform. Detail that cannot
 *  be read is not information, it is noise: at whole-lot zoom a 90 x 60 plate is
 *  ~9 px/m, so a 0.45 m label renders at 4 px. Forty flats' worth of that is
 *  what made the plan look crammed. */
function RoomLabel({ label, area, w, h, deg, px }:
  { label: string; area?: number; w: number; h: number; deg: number; px: number }) {
  // Width first: a name wider than its room is worse than no name. Then decide
  // whether the area line fits UNDER it — a debara is 1.4 m deep and two stacked
  // lines simply do not go in, so it gets the name alone rather than the two
  // printed over each other.
  const fit = Math.min(0.55, (w * 0.92) / (label.length * 0.66));
  if (fit < 0.22 || h < fit * 1.3) return null;
  if (fit * px < LOD.name) return null;
  const both = area !== undefined && h > fit * 2.6 && fit * px >= LOD.area;
  return (
    <g transform={`rotate(${-deg})`}>
      <text x={0} y={both ? -fit * 0.2 : fit * 0.35} textAnchor="middle"
            fontSize={fit} fontWeight={700} fill={ROOM_NAME}>{label}</text>
      {both && (
        <text x={0} y={fit * 1.15} textAnchor="middle" fontSize={fit * 0.8}
              fontWeight={600} fill={AREA_INK}>{area!.toFixed(2)} m²</text>
      )}
    </g>
  );
}

/** "acces clădire" at ground level: the porch on the facade and an arrow into
 *  the building. Same data the site plan draws (`site.entrances`), mirrored
 *  here so the ground floor shows how a resident gets in — on the floors above
 *  there is no door to the outside, so the caller only passes it for level 0.
 *  `deg` is the stall convention: outward heading = (sin, -cos). */
function EntranceMark({ e }: { e: SiteEntrance }) {
  const a = (e.deg * Math.PI) / 180;
  const ux = Math.sin(a), uy = -Math.cos(a);
  const px = -uy, py = ux;
  const tail = [e.x + ux * 3.4, e.y + uy * 3.4];
  // Annotation over the ground floor, not an object of its own — so it must not
  // eat a press meant for the bar underneath (the site plan has the clickable
  // one). Without this, pressing the porch resolves to no `data-pick` at all
  // and dismisses the panel.
  return (
    <g style={DECOR}>
      {e.porch?.length >= 3 && (
        <path d={d(e.porch)} fill="#dcd9d2" stroke={INK} strokeWidth={0.1} />
      )}
      <line x1={tail[0]} y1={tail[1]} x2={e.x + ux * 0.5} y2={e.y + uy * 0.5}
            stroke="#d32f2f" strokeWidth={0.22} />
      <polygon fill="#d32f2f"
        points={`${e.x + ux * 0.1},${e.y + uy * 0.1} ${e.x + ux * 1.1 + px * 0.55},${e.y + uy * 1.1 + py * 0.55} ${e.x + ux * 1.1 - px * 0.55},${e.y + uy * 1.1 - py * 0.55}`} />
    </g>
  );
}

export default function ApartmentView({
  level, showLabels = true, pxPerM = 40, entrances = [],
  interactive = false, selected, suprapus = false, ascunse,
}: {
  level: ApartmentLevel; showLabels?: boolean; pxPerM?: number;
  entrances?: SiteEntrance[];
  /** Desenat PESTE planul de arhitectură, ca strat, nu singur pe pânză.
   *
   *  Ce desena componenta asta era un plan ÎNTREG, fiindcă mult timp a fost
   *  singura care desena etajul de apartamente. De când planul se desenează
   *  din document (`StoreyView`: `Wall`, `Door`, `Window`, `Space`, `Flat`),
   *  jumătate din ea e a doua redare a acelorași lucruri — și nu una identică:
   *  pereții de aici sunt CONTURURI DE CAMERĂ îngroșate la 0,40 și 0,12 m,
   *  două constante ale browserului, în timp ce stratul de pereți desenează
   *  obiectele `Wall` la grosimea lor declarată. Două grosimi pentru același
   *  perete, exact felul de divergență pentru care locurile de parcare și-au
   *  primit `w`/`h` în document.
   *
   *  Cu `suprapus`, rămâne doar ce NU e în plan: corpurile și literele lor,
   *  barele cu coridorul lor, nucleele și mobilarea camerelor. */
  suprapus?: boolean;
  /** Straturile STINSE, aceeași mulțime pe care o citește `StoreyView`. Cât
   *  timp componenta desena un plan întreg, „stinge nucleele" n-avea sens; ca
   *  strat peste plan, fiecare lucru pe care îl aduce e o bifă separată. */
  ascunse?: ReadonlySet<string>;
  /** Mark every shape — a dwelling, one of its rooms, a balcony, a core part, a
   *  bar or its corridor — with what it is, so the Editor can name whatever the
   *  user presses. Off on the static export path. See `inspect.pickAttrs` for
   *  why this is an attribute and not a click handler. */
  interactive?: boolean;
  selected?: Selection | null;
}) {
  const lvl = level.level;
  const sel = selected && selected.level === lvl ? selected : null;
  const at = (kind: Selection["kind"], i: number, j?: number) =>
    // `sel.i` read optionally: a `storey` selection has no index, and it never
    // matches a drawn shape — it means "this whole floor".
    sel !== null && sel.kind === kind && (sel as { i?: number }).i === i
    && (j === undefined || (sel as { j?: number }).j === j);
  // `data-pick` on the shape, resolved by the Editor with `closest()`. Because
  // that walks UP the tree, a room's name label, its furniture and its door
  // leaf all resolve to the room, and the room resolves ahead of the flat it
  // sits inside — the drawing's own nesting does the disambiguation.
  const pick = (s: Selection): PickAttrs => interactive ? pickAttrs(s) : {};
  const outline = (poly: number[][], on: boolean) => on
    ? <path d={d(poly)} {...SEL_STROKE} /> : null;
  // Desenată singură pe pânză (nu ca strat), componenta arată tot: `ascunse` e
  // gol și `suprapus` fals, deci nimic nu se stinge.
  const vede = (st: string) => !suprapus || !ascunse?.has(st);

  return (
    <g className="pg-apartments">
      {/* Block outlines FIRST, under everything: a heavier line round each
          building and its name, the way A01 labels C1..C4 on the site plan.
          Without it an L-shaped block and two blocks a daylight distance apart
          draw identically. */}
      {(vede("circulatie") ? (level.buildings ?? []) : []).map((b, i) => {
        const g = oriented(b.poly, 0);
        return (
          <g key={`blk${b.id}`} {...pick({ kind: "building", level: lvl, i })}>
            {/* The outline is the pick target for the BLOCK — a stroke, not a
                fill, so a click only lands on the line itself and the flats
                inside stay reachable. */}
            <path d={d(b.poly)} fill="none" stroke={INK} strokeWidth={0.34}
                  strokeLinejoin="miter" strokeOpacity={0.5} />
            {outline(b.poly, at("building", i))}
            {showLabels && (
              <text x={g.cx} y={g.cy} textAnchor="middle"
                    fontSize={Math.min(g.w, g.h) * 0.16}
                    fill={INK} fillOpacity={0.22}
                    fontWeight={700} style={{ pointerEvents: "none" }}>
                {b.id}
              </text>
            )}
          </g>
        );
      })}

      {/* two passes, all strips then all corridors: the strips legitimately
          overlap at every junction, so interleaving them per bar let bar j's
          opaque strip paint out bar i's corridor — a visible notch in the
          salmon spine at every corner that read as an interrupted corridor */}
      {(vede("circulatie") ? level.bars : []).map((b, i) => (
        <g key={`bar${i}`} {...pick({ kind: "bar", level: lvl, i })}>
          <path d={d(b.poly)} fill="#f5f3ef" stroke="#8a8a8a" strokeWidth={0.12} />
          {outline(b.poly, at("bar", i))}
        </g>
      ))}
      {(vede("circulatie") ? level.bars : []).map((b, i) => (
        /* the common corridor — the spine every flat opens onto, in A-37's
           salmon so it reads as circulation and not as a room */
        <g key={`corr${i}`} {...pick({ kind: "corridor", level: lvl, i })}>
          <path d={d(b.corridor)} fill="#a9c4dc" fillOpacity={0.55}
                stroke="#b5745a" strokeWidth={0.08} />
          {outline(b.corridor, at("corridor", i))}
        </g>
      ))}

      {level.flats.map((f, i) => {
        const deg = f.deg ?? 0;
        return (
          <g key={`flat${i}`} {...pick({ kind: "flat", level: lvl, i })}>
            {/* balconies first, so the facade wall draws over their inner edge.
                ÎN CULOAREA LOCUINȚEI, nu gri neutru: balconul e al
                apartamentului («colorat după apartament», Cătălin, 1.10.2026),
                mai deschis ca să se citească afară. */}
            {(f.balconies ?? (f.balcony ? [f.balcony] : [])).map((b, k) => {
              const g = oriented(b.poly, deg);
              return (
                <g key={`balc${k}`} {...pick({ kind: "balcony", level: lvl, i })}>
                  <path d={d(b.poly)} fill={suprapus ? "none" : (FLAT_FILL[f.rooms] ?? "#dde3e8")}
                        fillOpacity={suprapus ? 0 : 0.55}
                        stroke={INK} strokeWidth={0.1} strokeDasharray="0.4 0.25" />
                  {outline(b.poly, at("balcony", i))}
                  {showLabels && !suprapus && g.w >= 1.0 && (
                    <g transform={`translate(${g.cx} ${g.cy}) rotate(${deg})`}>
                      <RoomLabel label="BALCON" area={b.area} w={g.w} h={g.h} deg={deg} px={pxPerM} />
                    </g>
                  )}
                </g>
              );
            })}
            {/* The dwelling's group is the pick target for the WHOLE flat; each
                room below is its own nested group, and `closest()` finds the
                nearest — so both are reachable. Press a bedroom for the
                bedroom, press the party wall or a door for the dwelling. */}
            {/* Conturul locuinței. Ca STRAT nu se mai desenează: `StoreyView`
                îl are (stratul «Apartamente»), iar îngroșarea de 0,40 de aici
                e un perete desenat a doua oară, la altă grosime decât cea din
                document. Rămâne pick target-ul locuinței — de asta grupul de
                deasupra există în ambele cazuri. */}
            {!suprapus && (
              <path d={d(f.poly)} fill={FLAT_FILL[f.rooms] ?? "#dde3e8"}
                    stroke={WALL} strokeWidth={W_STRUCT} strokeLinejoin="miter" />
            )}
            {f.layout.map((r, j) => {
              const g = oriented(r.poly as number[][], deg);
              return (
                <g key={j} {...pick({ kind: "flatRoom", level: lvl, i, j })}>
                  <path d={d(r.poly as number[][])}
                        fill={suprapus ? "none" : (ROOM_FILL[r.kind] ?? "none")}
                        fillOpacity={!suprapus && ROOM_FILL[r.kind] ? 0.85 : 0}
                        /* Conturul camerei, la fel: stratul «Spații» îl
                           desenează din `Space.poly`, iar 0,12 de aici e o
                           compartimentare redesenată. Ca strat rămâne doar
                           suprafața de apăsat, ca mobilierul dinăuntru să
                           poată fi inspectat. */
                        stroke={suprapus ? "none" : WALL}
                        strokeWidth={suprapus ? 0 : W_PART} strokeLinejoin="miter"
                        /* A habitable room is drawn with no fill of its own —
                           the dwelling's colour shows through — so without this
                           a press inside a living room falls past it to the
                           flat and the bedroom can never be inspected. */
                        pointerEvents={interactive ? "all" : undefined} />
                  {outline(r.poly as number[][], at("flatRoom", i, j))}
                  <g transform={`translate(${g.cx} ${g.cy}) rotate(${deg})`}>
                    {/* NU în `suprapus`: peste un plan de arhitectură,
                        mobilierul îl desenează `StoreyView` din aceleași
                        `Space`-uri, cu biblioteca bogată din `arhMobilier`.
                        Desenate amândouă, ieșeau două canapele suprapuse în
                        fiecare living — două păreri despre același obiect. */}
                    {!suprapus && vede("mobilier") && (
                      <Furniture kind={r.kind} w={g.w} h={g.h} px={pxPerM} />
                    )}
                    {showLabels && !suprapus && r.kind !== "hall" && (
                      <RoomLabel label={r.label.toUpperCase()} area={r.area}
                                 w={g.w} h={g.h} deg={deg} px={pxPerM} />
                    )}
                  </g>
                </g>
              );
            })}
            {(suprapus ? [] : (f.doors ?? [])).map((dr, j) => {
              // The opening is a gap in the wall, the leaf a line from the hinge,
              // and the swing a quarter arc from the tip back to the far jamb —
              // the way both reference plans draw it.
              const [hx, hy] = dr.hinge;
              const [tx, ty] = dr.tip;
              const far = dr.jambs[1] ?? dr.jambs[0];
              return (
                <g key={`door${j}`}>
                  <path d={`M${hx} ${hy}L${far[0]} ${far[1]}`}
                        stroke="#fff" strokeWidth={0.16} strokeLinecap="butt" />
                  <path d={`M${hx} ${hy}L${tx} ${ty}`}
                        stroke={INK} strokeWidth={0.07} fill="none" />
                  <path d={`M${tx} ${ty}A${dr.w} ${dr.w} 0 0 1 ${far[0]} ${far[1]}`}
                        stroke={INK} strokeWidth={0.05} fill="none"
                        strokeDasharray="0.18 0.14" />
                </g>
              );
            })}
            {/* the dwelling's own highlight goes LAST, over its own rooms */}
            {outline(f.poly, at("flat", i))}
          </g>
        );
      })}

      {(vede("nuclee") ? level.cores : []).map((c, i) => (
        <g key={`core${i}`}>
          {c.parts.map((p, j) => {
            // Drawn the way the sheets draw it (A-37; A09 CLADIRE 4): the lift
            // shafts white with both diagonals, the stair as a DOG-LEG — two
            // flights either side of a spine, treads across them, and an arrow
            // on the going flight. Ruled lines across a grey box read as a
            // hatch, not as a stair.
            const marks = [];
            if (p.kind === "scara" && p.w && p.h) {
              const n = 9, step = p.w / n;
              for (let k = 1; k < n; k++)
                marks.push(
                  <line key={`t${k}`} x1={-p.w / 2 + k * step} y1={-p.h / 2}
                        x2={-p.w / 2 + k * step} y2={p.h / 2}
                        stroke={INK} strokeWidth={0.04} />);
              // the spine between the two flights
              marks.push(<line key="spine" x1={-p.w / 2} y1={0} x2={p.w / 2} y2={0}
                               stroke={WALL} strokeWidth={0.09} />);
              // ...and the going arrow, on the lower flight
              const ay = p.h / 4;
              marks.push(<path key="up"
                               d={`M${-p.w / 2 + step * 0.6} ${ay}L${p.w / 2 - step * 0.6} ${ay}`}
                               stroke={INK} strokeWidth={0.05} fill="none" />);
              marks.push(<path key="uph"
                               d={`M${p.w / 2 - step * 1.2} ${ay - 0.16}L${p.w / 2 - step * 0.6} ${ay}L${p.w / 2 - step * 1.2} ${ay + 0.16}`}
                               stroke={INK} strokeWidth={0.05} fill="none" />);
            } else if (p.kind === "lift" && p.w && p.h) {
              marks.push(<line key="a" x1={-p.w / 2} y1={-p.h / 2}
                                x2={p.w / 2} y2={p.h / 2}
                                stroke={INK} strokeWidth={0.06} />);
              marks.push(<line key="b" x1={p.w / 2} y1={-p.h / 2}
                                x2={-p.w / 2} y2={p.h / 2}
                                stroke={INK} strokeWidth={0.06} />);
            }
            return (
              <g key={j} {...pick({ kind: "corePart", level: lvl, i, j })}>
                <path d={d(p.poly)}
                      fill={p.kind === "lift" ? "#ffffff"
                            : p.kind === "hol" ? "#f6f4ef" : "#eceff1"}
                      stroke={WALL}
                      strokeWidth={p.kind === "hol" ? W_PART : W_STRUCT}
                      strokeLinejoin="miter" />
                {outline(p.poly, at("corePart", i, j))}
                {p.cx !== undefined && p.cy !== undefined && (
                  <g transform={`translate(${p.cx} ${p.cy}) rotate(${p.deg ?? 0})`}>
                    {marks}
                    {showLabels && p.w && p.w > 1.4 && 0.5 * pxPerM >= LOD.coreLabel && (
                      <g transform={`rotate(${-(p.deg ?? 0)})`}>
                        <text x={0}
                              y={p.kind === "scara" ? -(p.h ?? 0) / 2 - 0.18 : 0.16}
                              textAnchor="middle" fontSize={0.44} fontWeight={700}
                              fill={ROOM_NAME}>
                          {/* Named from the part, not from a ternary whose
                              else-branch was "LIFT". The core gained a service
                              room beside the shafts (A-05 draws [ghenă][lift]
                              [lift]) and it came out labelled LIFT, so the plan
                              showed three lifts where the building has two. */}
                          {numeNucleu(p.kind) ?? p.label.toUpperCase()}
                        </text>
                        {/* the sheets print the landing's area under its name */}
                        {p.kind === "hol" && p.area !== undefined && (
                          <text x={0} y={0.72} textAnchor="middle" fontSize={0.36}
                                fontWeight={600} fill={AREA_INK}>
                            {p.area.toFixed(2)} m²
                          </text>
                        )}
                      </g>
                    )}
                  </g>
                )}
              </g>
            );
          })}
        </g>
      ))}

      {entrances.map((e, i) => <EntranceMark key={`ac${i}`} e={e} />)}
    </g>
  );
}
